'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const CartContext = createContext(null);

//  OLD single key
// const CART_KEY = 'athena_cart';

//  NEW keys
const GUEST_CART_KEY = 'athena_cart_guest';

function safeParse(value, fallback) {
  try {
    return JSON.parse(value) ?? fallback;
  } catch {
    return fallback;
  }
}

export const CartProvider = ({ children }) => {
  const { user } = useAuth();

  const [cart, setCart] = useState([]);
  const [initialized, setInitialized] = useState(false);

  //  USER BASED CART KEY
  const CART_KEY = useMemo(() => {
    if (user?.id) return `athena_cart_${user.id}`;
    return GUEST_CART_KEY;
  }, [user?.id]);

  //  1) Load cart from localStorage (when user changes OR first load)
  useEffect(() => {
    const saved = safeParse(localStorage.getItem(CART_KEY), []);
    setCart(Array.isArray(saved) ? saved : []);
    setInitialized(true);
  }, [CART_KEY]);

  //  2) Save to localStorage ONLY after initialized
  useEffect(() => {
    if (!initialized) return;

    localStorage.setItem(CART_KEY, JSON.stringify(cart));

    // 🔥 Navbar live update
    window.dispatchEvent(new Event('cartUpdated'));
  }, [cart, initialized, CART_KEY]);

  // Total items count
  const cartCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item?.quantity || 0), 0);
  }, [cart]);

 //  Add to cart
const addToCart = (product, qty = 1) => {
  if (!product?.id) return;

  setCart((prev) => {
    const existing = prev.find((x) => String(x.id) === String(product.id));

    if (existing) {
      return prev.map((x) =>
        String(x.id) === String(product.id)
          ? { ...x, quantity: (x.quantity || 0) + qty }
          : x
      );
    }

    return [
      ...prev,
      {
        id: product.id,
        name: product.name,
        partNumber: product.partNumber,
        image:
          typeof product.image === 'string'
            ? product.image
            : product.image?.url || '',
        alt: product.alt || product.name,
        price: product.price,
        quantity: qty,
        stock: product.stock ?? 0,
        minOrderQty: product.minOrderQty ?? 1,
        location: product.location ?? 'India',

        // ✅ DELIVERY FIX (IMPORTANT)
        handlingTime: product.handlingTime ?? 1,
        deliveryDays: product.deliveryDays ?? 10,

        // ✅ If product page already computed date/text then save it
        deliveryDateISO: product.deliveryDateISO ?? null,
        deliveryText: product.deliveryText ?? null,
      },
    ];
  });
};

  // ✅ Update quantity (same logic)
  const updateQuantity = (id, qty) => {
    setCart((prev) =>
      prev.map((x) =>
        String(x.id) === String(id) ? { ...x, quantity: qty } : x
      )
    );
  };

  //  Remove item (same logic)
  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((x) => String(x.id) !== String(id)));
  };

  //  Clear cart (UPDATED: also clears storage)
  const clearCart = () => {
    setCart([]);
    try {
      localStorage.removeItem(CART_KEY);
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (e) {
      console.error('clearCart error:', e);
    }
  };

  const value = {
    cart,
    setCart,
    cartCount,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};